import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Plus, UserPlus, Mail, Phone, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function Contacts() {
  const { activeSubId } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [tags, setTags] = useState([]);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", tags: "" });

  const load = useCallback(() => {
    if (!activeSubId) return;
    setLoading(true);
    const params = new URLSearchParams({ sub_account_id: activeSubId, search });
    if (tagFilter !== "all") params.append("tag", tagFilter);
    Promise.all([
      api.get(`/contacts?${params}`),
      api.get(`/contacts/tags?sub_account_id=${activeSubId}`),
    ]).then(([c, t]) => { setContacts(c.data); setTags(t.data); setLoading(false); });
  }, [activeSubId, search, tagFilter]);

  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);

  const toggleWa = async (c, e) => {
    e.stopPropagation();
    try {
      await api.put("/whatsapp/opt-in", { contact_id: c.id, opted_in: !c.whatsapp_opt_in });
      setContacts((prev) => prev.map((x) => x.id === c.id ? { ...x, whatsapp_opt_in: !c.whatsapp_opt_in } : x));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const create = async () => {
    setSaving(true);
    try {
      await api.post(`/contacts?sub_account_id=${activeSubId}`, {
        name: form.name, email: form.email || null, phone: form.phone || null,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Contact added");
      setOpen(false); setForm({ name: "", email: "", phone: "", tags: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">CRM</p>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mt-1">Contacts</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 font-semibold" data-testid="add-contact-button"><UserPlus className="w-4 h-4" />Add contact</Button>
          </DialogTrigger>
          <DialogContent data-testid="add-contact-dialog">
            <DialogHeader><DialogTitle>New contact</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5"><Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" /></div>
              <div className="space-y-1.5"><Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" /></div>
              <div className="space-y-1.5"><Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone-input" /></div>
              <div className="space-y-1.5"><Label>Tags (comma separated)</Label>
                <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="hot-lead, vip" data-testid="contact-tags-input" /></div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving || !form.name} data-testid="save-contact-button">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save contact"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search name, email, phone…" value={search}
            onChange={(e) => setSearch(e.target.value)} data-testid="contact-search-input" />
        </div>
        <Select value={tagFilter} onValueChange={setTagFilter}>
          <SelectTrigger className="w-[200px]" data-testid="tag-filter-select"><SelectValue placeholder="Filter by tag" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tags</SelectItem>
            {tags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-card border-border/70 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground" data-testid="contacts-empty">No contacts yet. Add your first one.</div>
        ) : (
          <div className="divide-y divide-border/60">
            {contacts.map((c) => (
              <div key={c.id} onClick={() => navigate(`/contacts/${c.id}`)}
                className="flex items-center gap-4 px-5 py-3.5 hover:bg-secondary/40 cursor-pointer transition-colors"
                data-testid={`contact-row-${c.id}`}>
                <div className="w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold shrink-0">
                  {c.name[0]?.toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{c.name}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {c.email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{c.email}</span>}
                    {c.phone && <span className="hidden sm:flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                  </div>
                </div>
                {c.phone && (
                  <button onClick={(e) => toggleWa(c, e)} title={c.whatsapp_opt_in ? "WhatsApp: opted in" : "WhatsApp: tap to opt in"}
                    className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${c.whatsapp_opt_in ? "bg-emerald-500/15 text-emerald-500" : "bg-secondary text-muted-foreground hover:text-foreground"}`}
                    data-testid={`wa-optin-${c.id}`}><MessageCircle className="w-4 h-4" /></button>
                )}
                <div className="hidden md:flex flex-wrap gap-1.5 justify-end max-w-[280px]">
                  {c.tags?.slice(0, 3).map((t) => <Badge key={t} variant="secondary" className="text-[11px]">{t}</Badge>)}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
